import React, { useState, useEffect } from "react";

//Utils
import { useForm, Controller } from "react-hook-form";

// Styling
import { makeStyles } from "@material-ui/core/styles";

// Components
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import ButtonsContainer from "./ButtonsContainer";
import MailchimpListsComboBox from "../../FormFields/MailchimpListsComboBox";
import MailchimpSegmentsComboBox from "../../FormFields/MailchimpSegmentsComboBox";
import Typography from "@material-ui/core/Typography";

const useStyles = makeStyles((theme) => ({
  form: {
    "& .MuiFormControl-root": { minWidth: 250 },
  },
  maxWidth: { width: "100%" },
}));

const APIKeysForm = ({
  onSubmit,
  onReset,
  isLoading,
  submitButtonLabel = "Save Settings",
  settingsList,
}) => {
  // Styles
  const classes = useStyles();

  // Builds the {id, name} pair a combo box expects, or null when unset, so the
  // Autocomplete stays controlled for segments that have never been picked.
  const settingPair = (idKey, nameKey) => {
    const idSetting = settingsList[idKey];
    const nameSetting = settingsList[nameKey];

    if (!idSetting || idSetting.value === "" || idSetting.value === null)
      return null;

    return {
      id: idSetting.value,
      name: nameSetting ? nameSetting.value : "",
    };
  };

  // Keeps `key` and `type` from the stored setting, and falls back to a core
  // setting when the row has not been seeded in the database yet.
  const settingEntry = (key, value) => ({
    ...(settingsList[key] || { key, type: "core" }),
    value,
  });

  // Form Setup
  const defaultValues = {
    google_analytics_id: settingsList.google_analytics_id.value,
    maps_api_key: settingsList.maps_api_key.value,
    reCaptchaSiteKey: settingsList.reCaptchaSiteKey.value,
    stripe_publishable_key: settingsList.stripe_publishable_key.value,
    mailchimp: {
      mailchimpListObj: settingPair("mailchimp_list_id", "mailchimp_list_name"),
      mailchimpSegmentObj: settingPair(
        "mailchimp_segment_id",
        "mailchimp_segment_name"
      ),
      mailchimpCheckoutSegmentObj: settingPair(
        "mailchimp_checkout_segment_id",
        "mailchimp_checkout_segment_name"
      ),
    },
  };
  const { handleSubmit, control, register } = useForm({
    defaultValues,
  });

  const formSubmit = (data) => {
    const {
      google_analytics_id,
      maps_api_key,
      reCaptchaSiteKey,
      stripe_publishable_key,
      mailchimp: {
        mailchimpListObj,
        mailchimpSegmentObj,
        mailchimpCheckoutSegmentObj,
      },
    } = data;

    const settingArray = [
      { ...settingsList.google_analytics_id, value: google_analytics_id },
      { ...settingsList.maps_api_key, value: maps_api_key },
      { ...settingsList.reCaptchaSiteKey, value: reCaptchaSiteKey },
      { ...settingsList.stripe_publishable_key, value: stripe_publishable_key },
      settingEntry(
        "mailchimp_list_id",
        mailchimpListObj ? String(mailchimpListObj.id) : ""
      ),
      settingEntry(
        "mailchimp_list_name",
        mailchimpListObj ? mailchimpListObj.name || "" : ""
      ),
      settingEntry(
        "mailchimp_segment_id",
        mailchimpSegmentObj ? String(mailchimpSegmentObj.id) : ""
      ),
      settingEntry(
        "mailchimp_segment_name",
        mailchimpSegmentObj ? mailchimpSegmentObj.name || "" : ""
      ),
      settingEntry(
        "mailchimp_checkout_segment_id",
        mailchimpCheckoutSegmentObj
          ? String(mailchimpCheckoutSegmentObj.id)
          : ""
      ),
      settingEntry(
        "mailchimp_checkout_segment_name",
        mailchimpCheckoutSegmentObj
          ? mailchimpCheckoutSegmentObj.name || ""
          : ""
      ),
    ];
    onSubmit(settingArray);
  };

  return (
    <form className={classes.form} onSubmit={handleSubmit(formSubmit)}>
      <Grid container spacing={5}>
        {/* Google Analytics Id*/}
        <Grid item xs={12}>
          <TextField
            inputRef={register}
            className={classes.maxWidth}
            name="google_analytics_id"
            label="Google Analytics ID"
            required
          />
        </Grid>
        {/* Google Maps Key */}
        <Grid item xs={12}>
          <TextField
            className={classes.maxWidth}
            inputRef={register}
            name="maps_api_key"
            label="Google Maps API Key"
            required
          />
        </Grid>
        {/* reCaptcha */}
        <Grid item xs={12}>
          <TextField
            inputRef={register}
            className={classes.maxWidth}
            name="reCaptchaSiteKey"
            label="reCaptcha Site Key"
            required
          />
        </Grid>
        {/* Stripe Publishable Key */}
        <Grid item xs={12}>
          <TextField
            inputRef={register}
            className={classes.maxWidth}
            name="stripe_publishable_key"
            label="Stripe Publishable Key"
            required
          />
        </Grid>

        <Grid item xs={12} style={{ paddingTop: 40, paddingBottom: 0 }}>
          <Typography component="p" variant="h6">
            Mailchimp
          </Typography>
        </Grid>

        {/* MailChimp List and Segment  */}
        <Grid container item xs={12} spacing={4}>
          <Controller
            name={`mailchimp`}
            control={control}
            defaultValue={defaultValues.mailchimp}
            render={(props) => (
              <MailChimpFields value={props.value} onChange={props.onChange} />
            )}
          />
        </Grid>

        {/* Disclaimer  */}
        <Grid item xs={12}>
          <Typography variant="h6">Disclaimers:</Typography>
          <Typography variant="body2">
            <ul>
              <li>
                The MailChimp API Key must be changed through the environment
                variables in the server.
              </li>
              <li>
                Both segments belong to the selected list: the Newsletter
                Segment is used for newsletter sign-ups, and the Checkout
                Segment for contacts coming from checkout.
              </li>
            </ul>
          </Typography>
        </Grid>

        <ButtonsContainer
          onReset={onReset}
          isLoading={isLoading}
          submitButtonLabel={submitButtonLabel}
        />
      </Grid>
    </form>
  );
};

const MailChimpFields = ({ value, onChange }) => {
  const [mailchimpListObj, setMailChimpListObj] = useState(
    value && value.mailchimpListObj ? value.mailchimpListObj : null
  );
  const [mailchimpSegmentObj, setMailchimpSegmentObj] = useState(
    value && value.mailchimpSegmentObj ? value.mailchimpSegmentObj : null
  );
  const [mailchimpCheckoutSegmentObj, setMailchimpCheckoutSegmentObj] =
    useState(
      value && value.mailchimpCheckoutSegmentObj
        ? value.mailchimpCheckoutSegmentObj
        : null
    );

  useEffect(() => {
    if (mailchimpListObj !== null) {
      onChange({
        mailchimpListObj,
        mailchimpSegmentObj,
        mailchimpCheckoutSegmentObj,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailchimpListObj, mailchimpSegmentObj, mailchimpCheckoutSegmentObj]);

  return (
    <>
      {/* MailChimp List  */}
      <Grid item xs={12} lg={4}>
        <MailchimpListsComboBox
          defaultValue={mailchimpListObj}
          onChange={setMailChimpListObj}
        />
      </Grid>

      {/* MailChimp Newsletter and Checkout Segments  */}
      {mailchimpListObj !== null && mailchimpListObj.id && (
        <>
          <Grid item xs={12} lg={4}>
            <MailchimpSegmentsComboBox
              id="mailchimpNewsletterSegment-select"
              label="Select a Newsletter Segment"
              list_id={mailchimpListObj.id}
              defaultValue={mailchimpSegmentObj}
              onChange={setMailchimpSegmentObj}
            />
          </Grid>

          <Grid item xs={12} lg={4}>
            <MailchimpSegmentsComboBox
              id="mailchimpCheckoutSegment-select"
              label="Select a Checkout Segment"
              list_id={mailchimpListObj.id}
              defaultValue={mailchimpCheckoutSegmentObj}
              onChange={setMailchimpCheckoutSegmentObj}
            />
          </Grid>
        </>
      )}
    </>
  );
};

export default APIKeysForm;
